/**
 * Type-safe readers for Yjs canvas node/edge structures.
 *
 * The Yjs API surfaces values as `unknown` because a CRDT cell can hold any
 * JSON value or a nested CRDT type. Direct `as Foo` casts hide structural
 * mismatches — a remote peer running an older schema can populate a field
 * with a string where the local code expects `{ x, y }` and the cast just
 * propagates the bad value into geometry math, where it surfaces as `NaN`
 * positions or runtime exceptions far from the source.
 *
 * These guards validate structure at the read site and return `null` on
 * mismatch, letting callers decide how to handle missing/corrupt data
 * (skip the node, fall back to a default, log, etc.).
 */
import * as Y from 'yjs';

export interface Point { x: number; y: number; }
export interface Size  { width: number; height: number; }

/**
 * Read a `{ x, y }` point from a Y.Map field. Returns `null` if the field
 * is missing or its value isn't an object with two finite numeric keys.
 */
export function readPoint(yMap: Y.Map<unknown>, key: string): Point | null {
  const v = yMap.get(key);
  if (v === null || typeof v !== 'object') return null;
  const obj = v as { x?: unknown; y?: unknown };
  if (typeof obj.x !== 'number' || typeof obj.y !== 'number') return null;
  return { x: obj.x, y: obj.y };
}

/**
 * Read a `{ width, height }` size from a node's `size` field. Returns
 * `null` when missing or when either dimension isn't a number.
 */
export function readSize(yNode: Y.Map<unknown>): Size | null {
  const v = yNode.get('size');
  if (v === null || typeof v !== 'object') return null;
  const obj = v as { width?: unknown; height?: unknown };
  if (typeof obj.width !== 'number' || typeof obj.height !== 'number') return null;
  return { width: obj.width, height: obj.height };
}

/**
 * Read a frame node's `data.childOrder` Y.Array. Returns `null` when
 * `data` isn't a Y.Map or `childOrder` isn't a Y.Array.
 */
export function readChildOrder(frame: Y.Map<unknown>): Y.Array<string> | null {
  const data = frame.get('data');
  if (!(data instanceof Y.Map)) return null;
  const childOrder = data.get('childOrder');
  if (!(childOrder instanceof Y.Array)) return null;
  return childOrder as Y.Array<string>;
}

/**
 * Read a string field from a node/edge's `data` Y.Map. Returns `null`
 * when missing or when the value isn't a string. Useful for
 * `data.stroke`, `data.markerStart`, etc.
 */
export function readDataString(yNode: Y.Map<unknown>, key: string): string | null {
  const data = yNode.get('data');
  if (!(data instanceof Y.Map)) return null;
  const v = data.get(key);
  return typeof v === 'string' ? v : null;
}
